import React, { useState } from "react";
import { X, Loader2, Mail, Lock, User, Phone, Cake } from "lucide-react";
import { errMessage } from "../lib/errors";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

export default function AuthModal() {
  const { modal, setModal, login, register } = useAuth();
  const { toast } = useToast();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [birthday, setBirthday] = useState("");
  const [loading, setLoading] = useState(false);

  const isOpen = modal === "login" || modal === "register";
  const isLogin = modal === "login";

  const close = () => {
    setModal(null);
    setEmail(""); setPassword(""); setName(""); setPhone(""); setBirthday("");
  };

  const switchMode = () => setModal(isLogin ? "register" : "login");

  const submit = async (e) => {
    e.preventDefault();
    // Manual validation (we removed HTML5 required so programmatic submits aren't blocked)
    if (!isLogin && !name.trim()) {
      toast({ title: "Missing name", description: "Please enter your name." });
      return;
    }
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast({ title: "Invalid email", description: "Please enter a valid email address." });
      return;
    }
    if (!password || password.length < 6) {
      toast({ title: "Password too short", description: "Password must be at least 6 characters." });
      return;
    }
    setLoading(true);
    try {
      const user = isLogin
        ? await login(email.trim(), password)
        : await register({ email: email.trim(), password, name: name.trim(), phone: phone.trim() || undefined, birthday: birthday.trim() || undefined });
      toast({
        title: isLogin ? `Welcome back, ${user.name || user.email}` : `Welcome to Mamamina's, ${user.name}`,
        description: isLogin ? "You are now signed in." : "Your account was created."
      });
      close();
    } catch (err) {
      toast({
        title: isLogin ? "Sign in failed" : "Sign up failed",
        description: errMessage(err)
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        onClick={close}
        className={`fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <div
        role="dialog"
        aria-modal="true"
        className={`fixed left-1/2 top-1/2 z-[100] w-full max-w-md -translate-x-1/2 -translate-y-1/2 px-6 transition-all duration-300 ${
          isOpen ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="bg-[var(--mm-bg)] border border-[var(--mm-accent)] p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="section-label mb-2">{isLogin ? "Sign In" : "Create Account"}</div>
              <h2 className="font-display italic text-3xl text-[var(--mm-cream)] leading-tight">
                {isLogin ? "Welcome back." : "Join the family."}
              </h2>
            </div>
            <button onClick={close} aria-label="Close" className="text-[var(--mm-cream)]">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={submit} noValidate className="space-y-3">
            {!isLogin && (
              <label className="block">
                <div className="flex items-center gap-2 mb-1 text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">
                  <User size={12} /> Name
                </div>
                <input
                  data-testid="auth-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
              </label>
            )}
            <label className="block">
              <div className="flex items-center gap-2 mb-1 text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">
                <Mail size={12} /> Email
              </div>
              <input
                data-testid="auth-email"
                type="text"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
              />
            </label>
            {!isLogin && (
              <label className="block">
                <div className="flex items-center gap-2 mb-1 text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">
                  <Phone size={12} /> Phone (optional)
                </div>
                <input
                  data-testid="auth-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="04xx xxx xxx"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
              </label>
            )}
            {!isLogin && (
              <label className="block">
                <div className="flex items-center gap-2 mb-1 text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">
                  <Cake size={12} /> Birthday DD/MM (optional — free dessert in your birthday week)
                </div>
                <input
                  data-testid="auth-birthday"
                  value={birthday}
                  onChange={(e) => setBirthday(e.target.value)}
                  placeholder="24/07"
                  maxLength={5}
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
              </label>
            )}
            <label className="block">
              <div className="flex items-center gap-2 mb-1 text-xs tracking-[0.2em] uppercase text-[var(--mm-cream-muted)]">
                <Lock size={12} /> Password
              </div>
              <input
                data-testid="auth-password"
                type="password"
                autoComplete={isLogin ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={isLogin ? "Your password" : "At least 6 characters"}
                className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
              />
            </label>

            <button
              type="submit"
              data-testid="auth-submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
            </button>

            <div className="flex items-center gap-3 my-1">
              <span className="h-px flex-1 bg-[var(--mm-line)]" />
              <span className="text-[0.62rem] tracking-[0.24em] uppercase text-[var(--mm-cream-muted)]">or</span>
              <span className="h-px flex-1 bg-[var(--mm-line)]" />
            </div>

            <button
              type="button"
              data-testid="auth-google-btn"
              onClick={() => {
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + "/account";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="btn-outline w-full flex items-center justify-center gap-2"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M21.35 11.1H12v2.9h5.35c-.5 2.5-2.6 4.3-5.35 4.3a5.8 5.8 0 1 1 0-11.6c1.48 0 2.82.56 3.85 1.48l2.17-2.17A8.9 8.9 0 0 0 12 3.1a8.9 8.9 0 1 0 0 17.8c5.14 0 8.85-3.61 8.85-8.7 0-.38-.04-.75-.1-1.1Z"/></svg>
              Continue with Google
            </button>

            <button
              type="button"
              onClick={switchMode}
              className="w-full text-center text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors mt-2"
            >
              {isLogin ? "New here? Create an account" : "Already have an account? Sign in"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
